/**
 * Studio service — the Video Studio ("director-in-a-box") backend surface. A STORYBOARD is a short video in
 * progress: ordered shots (each shaped by plain-English toggles + an optional start frame), a shared Cast
 * reference for character consistency, a length, a look, and a sound bed. This service is a thin wrapper over the
 * apiGateway routes; the toggle vocabulary + the AI Assist copilot both live server-side (no model names, no
 * technical params ever cross to the client).
 */
import { callFunction } from '../utils/apiClient';
import type { ConversationSummary } from './conversationService';

/** Plain-English toggle selections for one shot (keys resolved to prompt fragments server-side). */
export interface ShotToggles {
  setting?: string | null;
  light?: string | null;
  mood?: string | null;
  motion?: string | null;
}

/** One spoken line in a shot — a character says `line` in the chosen voice. */
export interface DialogueLine {
  character?: string;
  voiceId?: string;
  line: string;
}

/** How a shot is COVERED. Mirrors the server's vocabulary exactly; an unknown value is dropped there. */
export type ShotSize = 'ECU' | 'CU' | 'MCU' | 'MS' | 'MLS' | 'WS' | 'EWS';
export type CameraMove = 'static' | 'pan' | 'tilt' | 'push' | 'pull' | 'orbit' | 'handheld';
export interface ShotCamera {
  shot_size?: ShotSize;
  move?: CameraMove;
  pitch?: 'level' | 'high' | 'low';
}

/** How one clip enters from the previous — a plain-English footage transition (defaults to a cut). */
export type TransitionKind = 'cut' | 'fade' | 'dissolve';

/** One shot as edited/persisted (matches the backend value object). */
export interface StoryboardShot {
  /** The project location the scene opening at this shot happens in. Set on a band's FIRST shot. */
  location_id?: string;
  /** Which of the PROJECT's objects are in this shot — their locked wording is pasted in at render time. */
  prop_ids?: string[];
  /** Which of the PROJECT's people are in this shot — their SHORT appearance line reinforces the prompt. */
  character_ids?: string[];
  line?: string;
  toggles?: ShotToggles;
  freeText?: string;
  startFramePath?: string | null;
  /** A cheap PREVIEW keyframe (comic-strip panel) shown before the expensive render. */
  previewFramePath?: string | null;
  /** The judge's verdict on that panel: does it look like the cast member it was drawn from? Null until judged. */
  previewQuality?: PreviewQuality | null;
  clipPath?: string | null;
  clipFailed?: boolean;
  /** True when this clip came from the cheap DRAFT pass rather than the tier the creator publishes with.
   *  Set by the server on every successful clip, so a shot re-rendered properly clears it. */
  draft?: boolean;
  dialogue?: DialogueLine[];
  /** The transition INTO this clip from the previous one (shot 0 = the opening). Set on the connector seam. */
  transition?: TransitionKind;
  /** How long this shot HOLDS (pacing) — surfaced as Quick / Normal / Linger, never the raw keys. */
  weight?: 'brief' | 'normal' | 'hold';
  /** True when this shot stays in the SAME place as the previous one (a visual "same place" link on the strip). */
  continuesScene?: boolean;
  /** A plain-English SOUND EFFECT for this shot ("rain", "a door slam"); empty = quiet. */
  sfx?: string;
  /** Size, move and angle. The server has always accepted this; the client never sent it, so every drafted
   *  coverage decision was lost between the writer and the board (BUGS.md 2026-08-30). */
  camera?: ShotCamera;
  /** RE-MAKE version — bumped when the user re-makes this shot (drives per-shot regenerate + re-charge). */
  version?: number;
  /** APPROVED FOR SPEND — the creator is happy to pay to render this shot. Persisted, so a reload cannot
   *  quietly un-approve work. */
  locked?: boolean;
  /**
   * TAKE HISTORY — clips previously produced for this shot, newest first.
   *
   * The server archives the live clip here on every re-make (`archiveCurrentTake`), so a re-make is never a
   * one-way door: the creator already paid for the superseded clip and can compare and restore it. Bounded
   * server-side by MAX_TAKES_PER_SHOT.
   */
  takes?: ShotTake[];
}

/** One previously-produced clip for a shot, with the provenance needed to compare and restore it. */
export interface ShotTake {
  /** The archived clip (a Storage path — the media itself is never deleted). */
  clipPath: string;
  /** The shot `version` this take was produced under. */
  version: number;
  /** When it was superseded (ISO). */
  archived_at: string;
}

/** One music span on the audio lane — a bed covering shots [from_shot, to_shot] inclusive (0-based); non-overlapping. */
export interface MusicSpan {
  from_shot: number;
  to_shot: number;
  kind: 'song' | 'instrumental';
  mood?: string | null;
}

/** A selectable dialogue voice (human label; never a model name). */
export interface VoiceOption { id: string; label: string }
export interface VoiceCatalog { female: VoiceOption[]; male: VoiceOption[] }

export interface StoryboardSound {
  kind: 'song' | 'instrumental' | 'none';
  mood?: string | null;
  volume?: number;
  lyrics?: string | null;
}

/** One timed caption line, in milliseconds from the start of the episode. */
export interface CaptionCue {
  from_ms: number;
  to_ms: number;
  text: string;
}

export type StoryboardStatus = 'draft' | 'generating' | 'ready' | 'failed';
/** How fast/expensive a render runs — `draft` explores on the cheap tier, `final` is the one you can publish. */
export type RenderQuality = 'draft' | 'final';

export interface Storyboard {
  id: string;
  owner_id: string;
  /** The production's NAME — resolved by the server, which defaults an unnamed episode to `<series> ep.<n>`.
   *  Set here only by a RENAME (the explorer); the Studio never sends it, because the story is not a name. */
  title: string;
  /** THE CREATOR'S PREMISE — what the film is about, the Story page's one field. Its own field since
   *  2026-08-23; a production saved before that gets its old title back here, resolved server-side. */
  idea?: string;
  /** URL handle for /dashboard/{slug}. Absent on productions created before slugs existed — every route
   *  resolves slug OR id, so an older production still opens from its id. */
  slug?: string;
  conversation_id: string | null;
  series_id: string | null;
  episode_number: number;
  /** On the creator's store (RF3-STORE). Always an explicit act — never inferred from a finished render. */
  published?: boolean;
  published_at?: string | null;
  /** WHERE this episode plays, when it differs from the project's. Empty = it inherits (RF3-LEVEL C1). */
  setting?: string;
  /** WHICH of the project's characters are in this episode. Empty = the whole project cast is fair game. */
  featuring?: string;
  /**
   * The NATURE of this production — the work-stream preset key (micro-drama / product ad / …). It is a SERIES
   * fact (a series has one nature, its episodes inherit it), returned DENORMALIZED by `getStoryboard` so the
   * editor learns it in the call it already makes; sent on CREATE so the minted series is stamped with it.
   * Null/absent on a production created before the nature had a home.
   */
  stream?: string | null;
  cast_character_id: string | null;
  duration_seconds: number;
  look: string | null;
  /** How same-scene shots connect — surfaced as Flowing scenes / Distinct shots / Auto (never "chain"). */
  continuity_mode?: 'off' | 'chain' | 'auto' | null;
  video_alias: string | null;
  sound: StoryboardSound | null;
  music_spans: MusicSpan[];
  shots: StoryboardShot[];
  final_video_path: string | null;
  /** Timed captions for the finished cut, built at render from the dialogue and the clips' own slots. */
  captions?: CaptionCue[] | null;
  status: StoryboardStatus;
  created_at: string;
  updated_at: string;
}

export type StoryboardInput = Partial<Omit<Storyboard, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>;

/** A toggle group for the picker (chips only — labels are all the client renders). */
export interface ToggleGroup {
  id: 'setting' | 'light' | 'mood' | 'motion';
  label: string;
  options: Array<{ key: string; label: string }>;
}

/** The copilot's proposal for one shot. */
export interface ShotSuggestion {
  line: string;
  toggles: ShotToggles;
  rationale: string;
  /** Coverage, in the structured representation the render honours (size, move, angle). */
  camera?: ShotCamera;
  /** How long this beat holds relative to its neighbours. */
  weight?: 'brief' | 'normal' | 'hold';
  /** A short plain-English sound or ambience; absent for a shot carried by silence. */
  sfx?: string;
  /** Lines spoken in this shot — absent when nobody speaks. No voice: casting is the creator's call. */
  dialogue?: Array<{ character?: string; line: string }>;
  /** True when the score should come IN on this shot (only ever set for an episode that wants music). */
  music?: boolean;
  /** What happens WHEN inside the clip — the copilot works to the same timed template the director does. */
  timeline?: Array<{ from_s: number; to_s: number; action: string }>;
}

/** A creative specialist who can set a shot (a real agent, shown as a person). */
export interface StudioSpecialist {
  id: string;
  name: string;
  strength: string;
  avatar_icon: string;
}

// ── MEDIA CREW — staff a Studio with production roles (director/composer/writer/legal) + preset crews ──

export type CrewRole = 'director' | 'composer' | 'writer' | 'legal';
export interface CrewFlavor { agentId: string; flavor: string; hint: string; name: string; avatar_icon: string }
export interface CrewRoleDef { role: CrewRole; label: string; required: boolean; flavors: CrewFlavor[] }
export interface CrewPreset { key: string; label: string; hint: string; crew: Partial<Record<CrewRole, string>> }

/** The crew catalog: production roles + their flavor choices, and the named preset crews. */
export const getCrewCatalog = () =>
  callFunction<{ roles: CrewRoleDef[]; presets: CrewPreset[] }>('studioCrewCatalog', {});

/** Resolve a crew selection (a preset key and/or per-role picks) → the validated agent ids to staff the chat. */
export const resolveCrew = (input: { preset?: string; picks?: Partial<Record<CrewRole, string>> }) =>
  callFunction<{ agentIds: string[] }>('studioResolveCrew', input).then((r) => r.agentIds || []);

/** Context the copilot needs to propose the next shot. */
export interface AssistContext {
  idea: string;
  shotIndex: number;
  /** Coverage + pacing travel with each prior shot, so the copilot can VARY them rather than repeat them. */
  priorShots?: Array<{ line?: string; toggles?: ShotToggles; camera?: ShotCamera; weight?: string }>;
  currentLine?: string;
  /** The creator's own direction for this shot (the inspector's prose field) — it outranks the copilot's taste. */
  direction?: string;
  castAppearance?: string;
  /** Who can appear or speak, so a proposed line is attributed to a real character. */
  castNames?: string[];
  /** False for a silent episode, which is then never given a music cue. */
  wantsMusic?: boolean;
  /** Which specialist should set this shot (their strength shapes the suggestion). */
  agentId?: string;
  /**
   * The episode being written, so the SERVER can resolve this project's own cast, place, objects, voices and
   * sound state for this shot. Those names are load-bearing — a shot naming a character resolves to their
   * locked face and voice — so the copilot is handed the names that work rather than inventing plausible ones.
   */
  storyboardId?: string;
}

/**
 * The credit cost model (real WP costs from the server). Used for the SPEND CONFIRMATION before an
 * expensive render — never as an ambient price tag on a button (owner 2026-08-22).
 */
export interface StudioCosts {
  assistWp: number;
  videoWp: number;
  animateWp: number;
  imageWp: number;
  /** The per-shot DIRECTING fee — a rendered shot bills directing plus the clip itself. */
  directWp: number;
}

/** The result of asking a specialist to set a shot. */
export interface AssistResult {
  suggestion: ShotSuggestion | null;
  /** True when the user can't afford it (the model never ran). */
  needsTopUp?: boolean;
  /** WP charged (0 when nothing usable was produced). */
  wpCost?: number;
}

/** The plain-English toggle vocabulary + the cost model for the Story-step pickers. */
export const getStudioToggles = () =>
  callFunction<{ toggles: ToggleGroup[]; costs: StudioCosts }>('studioToggles', {});

/** The creative specialists available to help set a shot. */
export const getStudioSpecialists = () =>
  callFunction<{ specialists: StudioSpecialist[] }>('studioSpecialists', {}).then((r) => r.specialists || []);

/** Ask a specialist to set one shot (charge-on-success; needsTopUp when unaffordable). */
export const assistShot = (ctx: AssistContext) =>
  callFunction<AssistResult>('studioAssistShot', ctx);

/**
 * WRITE ONE FIELD FOR ME. The field says WHICH field it is and what is already in it; the brief lives on the
 * server, so this is not an open prompt endpoint. Returns plain text the field then owns — no accept/reject
 * ceremony, because the result is ordinary editable text and undo is the escape hatch.
 */
export const assistWrite = (field: string, current: string, seriesId?: string | null) =>
  callFunction<{ text: string; needsTopUp?: boolean; wpCost?: number }>('studioAssistWrite', { field, current, seriesId: seriesId || undefined });

/** A drafted shot from the beat-sheet (a plain-English line + its dramatic beat). */
/**
 * ONE DRAFTED SHOT, as the writer produced it.
 *
 * This used to be `{ line, beat }` — a narrower mirror of what the server actually returns, so `sfx`, `weight`
 * and `continuesScene` were drafted, serialized, and then dropped on the floor by the type itself. The writer
 * was doing sound design and pacing that never reached a shot. Widened to the server's real shape, and kept
 * in step with it deliberately: a narrower client type is silent data loss, not a simplification.
 */
export interface ShotDraft {
  line: string;
  beat?: string;
  weight?: 'brief' | 'normal' | 'hold';
  continuesScene?: boolean;
  sfx?: string;
  /** Who speaks and what they say — structured, so it can be VOICED rather than described. */
  dialogue?: { character: string; line: string; delivery?: 'speech' | 'narration' }[];
  camera?: ShotCamera;
  /** How this shot ENTERS from the one before. Set by the director's pass. */
  transition?: TransitionKind;
  /** WHO is in it (project cast ids) and WHERE (a project place id), linked by name on the server. */
  character_ids?: string[];
  location_id?: string;
}

/** A one-tap starter (label + a premise to seed the idea) for the active stream, on its catalog shelf. */
export interface StudioGenre { key: string; label: string; premise: string; category?: string }

/** The one-tap starters for a stream — micro-drama tropes, ad formats, music-video treatments, explainer shapes. */
export const getStudioGenres = (stream?: string | null) =>
  callFunction<{ genres: StudioGenre[] }>('studioGenres', stream ? { stream } : {}).then((r) => r.genres || []);

/** A media model ALIAS the creator picks between — an outcome name + guidance + credit cost, NEVER a model name. */
export interface MediaAlias {
  key: string;
  label: string;
  description: string;
  tip: string;
  recommended: boolean;
  creditCost: number;
  /** Always 'WP' — the one currency, kept on the row so a stored draft is self-describing. */
  currency: string;
}

/** The video "which look?" alias menu (outcome names + tips + credit cost; the real models stay server-side). */
export const getMediaAliases = () =>
  callFunction<{ video: MediaAlias[] }>('studioMediaAliases', {}).then((r) => r.video || []);

/** Draft a beat-driven micro-drama shot list (optionally continuing a series from its last cliffhanger). */
/** A format-fit advisory on a drafted episode (W13) — the proven micro-drama shape the draft missed. */
export interface FormatIssue { code: 'no_cliffhanger' | 'no_hook' | 'flat_pacing' | 'too_short'; message: string }

/** `seriesId` lets the server plan the episode against the series STORY BIBLE (W13c) — durable continuity
 *  beyond the previous cliffhanger, which is what keeps a long-running series consistent. */
/**
 * Draft this episode's beats. `episode` is what the episode NARROWS to inside its project's world — where it
 * plays and who is in it (RF3-LEVEL C1). Omitted or empty simply inherits the project's answers.
 */
export const draftBeatSheet = (
  idea: string, shotCount?: number, genre?: string, continuesFrom?: string, seriesId?: string, writerId?: string,
  episode?: { setting?: string; featuring?: string; episodeNumber?: number; storyboardId?: string | null },
) =>
  callFunction<{ shots: ShotDraft[]; music?: MusicSpan[]; formatIssues?: FormatIssue[]; needsTopUp?: boolean; wpCost?: number; trial?: boolean }>('studioBeatSheet', {
    idea, shotCount, genre, continuesFrom, seriesId, writerId,
    // The episode being written: the server covers it with the free first storyboard when it is the trial's.
    storyboardId: episode?.storyboardId || undefined,
    setting: episode?.setting || undefined, featuring: episode?.featuring || undefined,
    // Which row of the project's plan this episode IS — the server resolves the planned beat from it.
    episodeNumber: episode?.episodeNumber,
  });

/** One drafted ad-hook variation: the creative angle it explores + its ordered shots (Shovel B). */
export interface HookVariation { angle: string; angleLabel: string; shots: ShotDraft[] }

/** Draft a BATCH of vertical ad-hook variations from one product idea, each on a distinct proven angle (for A/B). */
export const draftHookVariations = (idea: string, count?: number, shotCount?: number) =>
  callFunction<{ variations: HookVariation[]; needsTopUp?: boolean; wpCost?: number }>('studioHookVariations', { idea, count, shotCount });

/** Generate a shot's start frame from a prompt (charge-on-success image credits). Returns its Storage path. */
/**
 * Generate a frame from a prompt, optionally GROUNDED by a reference image the creator uploaded (owner law
 * 2026-08-29: prompt + reference beats prompt alone). With a reference the request routes server-side to the
 * reference-capable model; without one nothing changes.
 */
/** `stub` = a local placeholder, not a generation. Surfaces so the UI can say so rather than imply a result. */
export const generateStudioFrame = (prompt: string, conversationId: string | null, referencePath?: string | null) =>
  callFunction<{ path?: string; cost?: number; stub?: boolean; needsTopUp?: boolean; providerDown?: boolean }>('studioGenerateFrame', { prompt, conversationId, referencePath: referencePath || undefined });

/**
 * A REFERENCE PLATE for something the project already holds — a place, an object or a character's face.
 *
 * The caller says WHAT to picture; the server composes HOW from the project's own facts and its look (F2).
 * That is the difference from `generateStudioFrame`, which takes a prompt the client wrote: a plate has house
 * rules that must not vary between surfaces, and three client-side lead strings had already drifted apart.
 * The composed wording comes back so the surface can show what was actually asked for.
 */
export type PlateKind = 'place' | 'object' | 'face';
export const generateReferencePlate = (seriesId: string, kind: PlateKind, itemId: string, referencePath?: string | null) =>
  callFunction<{
    path?: string | null; cost?: number; prompt?: string; stub?: boolean;
    /** False when the row has nothing written yet — nothing was generated and nothing was charged. */
    described?: boolean;
    needsTopUp?: boolean; providerDown?: boolean;
  }>('studioReferencePlate', { seriesId, kind, itemId, referencePath: referencePath || undefined });

/**
 * WHAT THIS SHOT WILL ACTUALLY BE SHOWN — a dry run of the render's own reference resolution (F3).
 *
 * Reads only: nothing is generated and nothing is charged. `dropped` is the half that matters most, because a
 * missing picture, a full slot budget and a photoreal face held by description are indistinguishable from the
 * creator's seat and each needs a different fix.
 */
export interface ShotReferenceView { kind: 'face' | 'place' | 'object'; name: string; path: string }
export interface DroppedReferenceView { kind: 'face' | 'place' | 'object'; name: string; reason: string }
export const getShotReferences = (storyboardId: string, index: number) =>
  callFunction<{ mode: string; sent: ShotReferenceView[]; dropped: DroppedReferenceView[] }>(
    'studioShotReferences', { storyboardId, index },
  );

export const listStoryboards = () =>
  callFunction<{ storyboards: Storyboard[] }>('listStoryboards', {}).then((r) => r.storyboards || []);

export const getStoryboard = (id: string) => callFunction<Storyboard>('getStoryboard', { id });

/** A new episode changes the explorer's shelf (and may mint a series), so it is announced like a series write. */
export const createStoryboard = async (input: StoryboardInput) => {
  const sb = await callFunction<Storyboard>('createStoryboard', input);
  announceSeriesChanged();
  return sb;
};

export const updateStoryboard = (id: string, patch: StoryboardInput) =>
  callFunction<Storyboard>('updateStoryboard', { id, ...patch });

export const deleteStoryboard = (id: string) =>
  callFunction<{ deleted: boolean }>('deleteStoryboard', { id });

/** Queue a storyboard for rendering (charge-per-clip happens server-side on success). Poll getStoryboard for status.
 *  `quality` picks the speed: a draft explores cheaply, a final render is the one that can be published. */
export const renderStoryboard = (id: string, quality: RenderQuality = 'final') =>
  callFunction<Storyboard & { needsTopUp?: boolean; providerDown?: boolean; message?: string }>('renderStoryboard', { id, quality });

/** RE-MAKE one shot — regenerate + re-charge (directing + the clip) for JUST that shot, keeping the others. Poll getStoryboard. */
export const remakeShot = (id: string, shotIndex: number, quality: RenderQuality = 'final') =>
  callFunction<Storyboard & { needsTopUp?: boolean; providerDown?: boolean; message?: string }>('studioRemakeShot', { id, shotIndex, quality });

/**
 * RECRUIT an agent onto this production's crew — the one door every hire in the Studio goes through.
 *
 * Recruiting IS joining the crew chat: the server creates that chat at the FIRST recruit and stamps the link
 * on the production itself, so the client never has to own (or race) it. Free — recruiting produces no output.
 */
/**
 * Put an agent on the production's crew. Name the SERIES when recruiting from the project surface — the crew
 * belongs to the project, and naming a storyboard there would mint an episode nobody asked for.
 */
export const recruitCrewAgent = (
  target: { storyboardId?: string; seriesId?: string },
  agentId: string,
  /**
   * Who is being REPLACED, when this seat already has somebody in it. Without it recruiting was add-only, so
   * choosing a different agent for a filled craft left the old one aboard and the seat unchanged (BUGS.md
   * 2026-09-13). Naming them makes a swap one action rather than a removal the creator has to remember.
   */
  replacesAgentId?: string,
) =>
  callFunction<{ conversationId: string; conversation: ConversationSummary; created: boolean }>(
    'recruitCrewAgent', { ...target, agentId, replacesAgentId: replacesAgentId || undefined },
  );

/** Take an agent off the crew. Refused server-side when they are the last one — replace them instead. */
export const dismissCrewAgent = (target: { storyboardId?: string; seriesId?: string }, agentId: string) =>
  callFunction<{ conversationId: string; conversation: ConversationSummary; removed: boolean }>(
    'dismissCrewAgent', { ...target, agentId },
  );

/** One in-flight render, as the persistent tracker needs it. */
export interface ActiveRender {
  id: string;
  /** The /dashboard/{slug} handle, so the tracker can take the creator back to it. */
  slug: string;
  title: string;
  total: number;
  done: number;
}

/** The caller's in-flight renders. Small by design — this is polled from every screen. */
export const getActiveRenders = () =>
  callFunction<{ renders: ActiveRender[] }>('studioActiveRenders', {}).then((r) => r.renders || []);

/** Promote an archived take back to being this shot's live clip. Free — the clip already exists. */
export const restoreTake = (id: string, shotIndex: number, clipPath: string) =>
  callFunction<Storyboard>('studioRestoreTake', { id, shotIndex, clipPath });

/** The shot judge's verdict on one preview panel (QUALITY-2). */
export interface PreviewQuality {
  overall: number;
  likeness: number | null;
  passes: boolean;
  /** Who the panel was checked against. */
  cast: string | null;
  /** Appearance details the panel got wrong, e.g. "shaved head". */
  misses: string[];
  defects: string[];
}

/**
 * PRE-GENERATION preview — cheap keyframes (a comic strip) before the expensive render. Affordability-bounded (image
 * credits each). `redraw` names shots to draw again even though nothing in them changed (charged like any panel).
 */
export const previewStoryboardFrames = (id: string, force = false, redraw?: number[]) =>
  callFunction<{ results: Array<{ index: number; previewFramePath?: string; previewQuality?: PreviewQuality | null; failed?: boolean }>; generated: number; needsTopUp?: boolean; providerDown?: boolean }>(
    'studioPreviewFrames', redraw?.length ? { id, force, redraw } : { id, force });

// ── Micro-film SERIES — group videos into an episodic collection in the user's library ──

/** A series: a named collection of video episodes (each episode is a Storyboard). */
/** The durable series continuity every later episode is planned against (W13c). All fields optional. */
/**
 * THE PROJECT'S HIGH-LEVEL INFO (owner 2026-08-24: *"high-level plot, characters, world settings (time, where,
 * etc) at project level"*). Every episode is written against this, so it lives on the SERIES, not on any one
 * episode. `era` + `setting` are separate from `premise` deliberately — one paragraph asked to carry the plot
 * AND the world reliably returns only the plot.
 */
export interface SeriesBible {
  /** The high-level PLOT — what this whole thing is about, above any one episode. */
  premise?: string;
  /** WHEN it happens. */
  era?: string;
  /** WHERE it happens. */
  setting?: string;
  /** HOW IT LOOKS — one short line of visual style, used by every reference plate and every shot. */
  look?: string;
  /** Who keeps coming back, and what drives them. */
  characters?: string;
  arc?: string;
  established?: string;
  /** The one to three places this show happens in. A location is a PROJECT fact — every episode reuses it. */
  locations?: SeriesLocation[];
  /** HERO objects only — handled, recurring, or beat-driving. Background dressing is never a row. */
  props?: SeriesProp[];
  /** The people, in depth — the foundation dialogue and scripts are written from. Supersedes `characters`. */
  cast?: SeriesCharacter[];
  /** How many episodes the creator is aiming at. Defaults to 12; the market's unit is 60-100, stated not imposed. */
  episode_target?: number;
  /** One row per planned episode. A row is a PLAN until it is opened — planning mints no documents. */
  plan?: PlannedEpisode[];
}

/**
 * One person, in depth. The split down the middle is deliberate: `appearance` is the ONLY field a visual
 * prompt reads and is deliberately short (a long one makes the face drift); everything else exists so that
 * dialogue sounds like this person and nobody else, and no image model ever sees it.
 */
export interface SeriesCharacter {
  id: string;
  name: string;
  /** SHORT. Two or three anchor attributes — the visual prompt reads only this. */
  appearance?: string;
  history?: string;
  motivation?: string;
  personality?: string;
  unique?: string;
  role?: string;
  /** The Character SHEET holding their locked face + turnarounds. Absent = written, not yet cast. */
  character_id?: string | null;
  /** How they SOUND, chosen once at show level so a person does not change voice between episodes. */
  voice_id?: string | null;
  /** The voice's human label, denormalized so a cast row can name it without loading the catalog. */
  voice_label?: string | null;
  /**
   * HOW THEY SOUND, IN WORDS — read by the models that perform a line from the PROMPT, which is most of them.
   * Not the same fact as `voice_id`: that is a catalog pick driving the separate synthesis path. Capped like
   * `appearance`, and for the same reason — re-describing a voice at length is what makes it drift.
   */
  voice_style?: string;
}

/** A recurring object, worded once, for the same reason a location is: a model re-invents what it cannot see. */
export interface SeriesProp {
  id: string;
  name: string;
  description?: string;
  held_by?: string;
  /** A reference plate for this object — uploaded, or generated from the description. */
  image_path?: string | null;
  /** The wording the server used to make that plate (F2). Read-only here; the server writes it. */
  image_prompt?: string;
}

/**
 * One planned episode. Deliberately thin — a title, the one escalation it carries, and what it ends on. Three
 * fields are what let episode 7 know its job; planning sixty episodes in detail ships nothing.
 */
export interface PlannedEpisode {
  n: number;
  working_title?: string;
  beat?: string;
  cliffhanger?: string;
  /** Set once this row has become a real episode. */
  storyboard_id?: string | null;
}

/** Draft the run — one row per episode, from the project's plot and world. Mints no documents. */
export const planEpisodes = (plot: string, count: number, seriesId?: string | null) =>
  callFunction<{ plan: PlannedEpisode[] | null; needsTopUp?: boolean; wpCost?: number }>('studioPlanEpisodes', { plot, count, seriesId: seriesId || undefined });

/** What a story NEEDS, proposed from its plot — characters, hero objects, places. Merged, never applied raw. */
export interface StoryManifest {
  characters: Array<{ name: string; note?: string }>;
  props: Array<{ name: string; description?: string; held_by?: string }>;
  locations: Array<{ name: string; description?: string; anchor_object?: string }>;
}

/**
 * Ask the writer what this story needs. Returns a PROPOSAL — the caller merges it against what the creator
 * already has, matched by name, so re-running never destroys work.
 */
export const getStoryManifest = (plot: string, seriesId?: string | null) =>
  callFunction<{ manifest: StoryManifest | null; needsTopUp?: boolean; wpCost?: number }>('studioStoryManifest', { plot, seriesId: seriesId || undefined });

/**
 * One place, worded once. `anchor_object` is the load-bearing field: production practice is to name ONE thing
 * in the set that cannot change between shots ("a red brick wall, a brass light fixture"), because a model
 * reproduces a named object far more reliably than a mood.
 */
export interface SeriesLocation {
  id: string;
  name: string;
  description?: string;
  anchor_object?: string;
  /** A reference plate for this place — uploaded, or generated from the description. */
  image_path?: string | null;
  /** The wording the server used to make that plate (F2). Read-only here; the server writes it. */
  image_prompt?: string;
}

/** What a project must state before its episodes have anything to be written against. Mirrors the server. */
export const REQUIRED_BIBLE_FIELDS: Array<keyof SeriesBible> = ['premise', 'era', 'setting', 'characters'];

export interface Series {
  id: string;
  owner_id: string;
  title: string;
  description: string;
  cover_path: string | null;
  episode_count: number;
  bible?: SeriesBible;
  /** The plot shape the whole show is written against — a plot-library key. Every episode inherits it. */
  genre?: string | null;
  created_at: string;
  updated_at: string;
}

export type SeriesInput = Partial<Omit<Series, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>;

export const createSeries = (input: SeriesInput) => callFunction<Series>('createSeries', input);

export const listSeries = () =>
  callFunction<{ series: Series[] }>('listSeries', {}).then((r) => r.series || []);

/** A series + its ordered episode catalogue. */
export const getSeries = (id: string) =>
  callFunction<{ series: Series; episodes: Storyboard[] }>('getSeries', { id });

/**
 * SERIES-LIST CHANGED — announced centrally, so a surface showing the list cannot go stale (BUGS.md 2026-08-30).
 *
 * The Project Explorer fetched its series once and kept the name it saw at startup, so renaming a project on
 * the Project page left the pane beside it showing the old title — with no way for a creator to tell whether
 * the rename had saved. Two components owned the same fact and neither told the other.
 *
 * Announced from the SERVICE rather than from either component, because the service is the one place every
 * writer already passes through: a future rename from a third surface is covered without touching this again.
 * A CustomEvent on `window` follows the `wt:` idiom already used for version drift in `apiClient`; a store
 * would be the heavier answer for one list that one pane reads.
 */
function announceSeriesChanged(): void {
  try { window.dispatchEvent(new CustomEvent('wt:series-changed')); }
  catch { /* never fail a write because a listener could not be notified */ }
}

export const SERIES_CHANGED_EVENT = 'wt:series-changed';

export const updateSeries = async (id: string, patch: SeriesInput) => {
  const series = await callFunction<Series>('updateSeries', { id, ...patch });
  announceSeriesChanged();
  return series;
};

export const deleteSeries = async (id: string) => {
  const out = await callFunction<{ deleted: boolean }>('deleteSeries', { id });
  announceSeriesChanged();
  return out;
};

// ── Copyright / provenance paperwork (subscription + legal-agent gated, WP-charged) ──

export type CopyrightDocType = 'authorship' | 'license' | 'provenance';

export interface CopyrightResult {
  /** Set when blocked: needs a subscription, or a legal agent in the group. */
  locked?: 'subscription' | 'legal_agent';
  needsTopUp?: boolean;
  legalAgentId?: string;
  doc?: string;
  docType?: CopyrightDocType;
  wpCost?: number;
}

/** Generate a copyright/provenance document for a saved video (charge-on-success WP; may return a locked state). */
export const generateCopyrightDoc = (storyboardId: string, docType: CopyrightDocType, memberProfileIds: string[]) =>
  callFunction<CopyrightResult>('copyrightDoc', { storyboardId, docType, memberProfileIds });

/** A saved paperwork document, associated with a video. */
export interface LegalDoc {
  id: string;
  owner_id: string;
  storyboard_id: string;
  doc_type: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

/** Save a generated document to the user's paperwork (associated with the video). */
export const saveLegalDoc = (input: { storyboardId: string; docType: string; title: string; content: string }) =>
  callFunction<LegalDoc>('saveLegalDoc', input);

/** The paperwork saved for a video (or all of the user's paperwork when no id is given). */
export const listLegalDocs = (storyboardId?: string) =>
  callFunction<{ docs: LegalDoc[] }>('listLegalDocs', { storyboardId }).then((r) => r.docs || []);

export const deleteLegalDoc = (id: string) =>
  callFunction<{ deleted: boolean }>('deleteLegalDoc', { id });

// ── Dialogue voices ──

/** The selectable dialogue voices (female/male pools). */
export const getStudioVoices = () =>
  callFunction<{ voices: VoiceCatalog }>('studioVoices', {}).then((r) => r.voices);

/** Audition a voice → an MP3 data-URL to play (free, short sample). */
export const previewStudioVoice = (voiceId: string) =>
  callFunction<{ voiceId: string; audio: string }>('studioVoicePreview', { voiceId }).then((r) => r.audio);

/**
 * THE CREATOR'S STOREFRONT (PRODUCT.md's Distribute pillar). `short_id` is the permanent public address —
 * `/s/{short_id}` IS the store's URL, minted once and never rotated, because every link to it would die.
 */
export interface Store {
  short_id: string;
  title: string;
  tagline: string;
}

/** One production as a stranger sees it on a store page — a handpicked projection, never the document. */
export interface StoreListing {
  id: string;
  slug: string;
  title: string;
  idea: string;
  poster_path: string | null;
  video_path: string | null;
  episode_number: number;
  series_title: string;
  published_at: string | null;
  /** True when this episode is past the free run. The film itself is withheld server-side, not hidden here. */
  locked?: boolean;
  /** What unlocking it costs, in credits. Only meaningful when `locked`. */
  price_wp?: number;
}

/** What the store hands back for one episode: whether it is behind the ladder, and the film if it is not. */
export interface EpisodeWatch {
  found?: boolean;
  locked: boolean;
  price_wp: number;
  /** A short-lived signed url. The storage path is owner-readable, so a path would be unplayable here. */
  video_url: string | null;
  captions?: CaptionCue[] | null;
}

/**
 * WATCH A FREE EPISODE — no account needed, deliberately. The free opening run is the whole acquisition
 * mechanism; a sign-in in front of episode one gates the thing that is meant to be ungated.
 */
export const getEpisodeStream = (id: string) =>
  callFunction<EpisodeWatch>('getEpisodeStream', { id }, { requireAuth: false });

/**
 * MAY I WATCH THIS ONE? Authenticated, and separate from the route above for a structural reason: the public
 * storefront is never told who is asking, so "have YOU unlocked this?" cannot be answered there.
 */
export const getEpisodeAccess = (id: string) =>
  callFunction<EpisodeWatch>('getEpisodeAccess', { id });

/** Buy one episode with credits. Idempotent server-side: a double tap cannot pay twice. */
export const unlockEpisode = (id: string) =>
  callFunction<{ unlocked?: boolean; alreadyOwned?: boolean; needsTopUp?: boolean; price_wp?: number; video_url: string | null; captions?: CaptionCue[] | null }>('unlockEpisode', { id });

/** The caller's own store, minted on first ask, with everything currently listed in it. */
export const getMyStore = () =>
  callFunction<{ store: Store; listings: StoreListing[] }>('getMyStore', {});

/** Rename the storefront. The address is deliberately not part of this — it never changes. */
export const updateStore = (patch: { title?: string; tagline?: string }) =>
  callFunction<{ store: Store }>('updateStore', patch);

/** A store by its public handle. No auth — this is the link a creator shares. `requireAuth: false` is what makes
 *  that true: without it the client refused to send the request when nobody was signed in (or before a signed-in
 *  session restored), and every visitor saw "No store here" (BUGS.md 2026-09-27). */
export const getPublicStore = (storeId: string) =>
  callFunction<{ store: Store | null; listings: StoreListing[] }>('getPublicStore', { storeId }, { requireAuth: false });

/** List a production on the store, or take it down. Publishing is always an explicit act. */
export const setProductionPublished = (id: string, published: boolean) =>
  callFunction<{ storyboard: Storyboard }>('setProductionPublished', { id, published });

